import type { Ref } from 'vue';
import { createInjection } from 'src/lib/createInjection';
import { useMapSelectionCore, type MapSelectionCoreContext } from 'src/composables/useMapSelectionCore';
import { useMapSelectionCommentCrud, type MapSelectionCommentCrudContext } from 'src/composables/useMapSelectionCommentCrud';
import { useMapSelectionGeoItemNav, type MapSelectionGeoItemNavContext } from 'src/composables/useMapSelectionGeoItemNav';
import type { CommentIndexParams } from 'src/models/apis/comment/commentRequest';
import type { MovieList } from 'src/models/movie';
import type { GeoItemsMeta } from 'src/models/geoItem';
import type { IExtremeMapRef, QueryArea } from 'src/models';
import type { IGeoItemSearchRef } from 'src/models/geoItemSearch';
import type { SimpleModalState } from 'src/models/top';

// 3つの内部composableの公開キーをそのまま束ねたstore型（キー集合は分割前と同一）
export type MapSelectionStore =
  MapSelectionCoreContext &
  MapSelectionCommentCrudContext &
  MapSelectionGeoItemNavContext;

const { provide: provideMapSelectionCtx, inject: injectMapSelectionCtx } =
  createInjection<MapSelectionStore>('useMapSelection');

export function useMapSelectionStore(): MapSelectionStore {
  return injectMapSelectionCtx();
}

/**
 * 地図選択まわりのストアを組み立てて provide するファサード。
 * 内部は責務ごとに以下の3つのcomposableへ分割し、すべて core への一方向依存とする:
 *   - useMapSelectionCore: 地図クリック/選択コア（+動画再生連携）
 *   - useMapSelectionCommentCrud: コメント(付箋)CRUD
 *   - useMapSelectionGeoItemNav: タブ内geoItemナビゲーション
 * 戻り値のキー集合は分割前と完全に同一に維持すること（呼び出し元は無修正）。
 *
 * provide/injectは親→子方向のみで、provider自身はこのstoreをinjectできないため、
 * provider側でも利用できるようprovideするだけでなくstoreを返り値でも渡す。
 */
export function provideMapSelection(deps: {
  geoItemsMeta: GeoItemsMeta;
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  resizeMap: () => void;
  simpleModal: SimpleModalState;
  refreshCommentTab: () => void;
  afterMoviePlayClick: (obj: MovieList) => void;
  doAreaMovieSearch: (queryArea: QueryArea) => void;
  changeGeoItemLayers: (dataName: string) => Promise<void>;
  getCommentSearchRef: () => IGeoItemSearchRef<CommentIndexParams> | undefined;
  isMovieSearchResultModeNone: () => boolean;
  isMovieSearchResultModeArea: () => boolean;
  getAreaQueryRadiusMeter: () => number;
}): MapSelectionStore {
  const core = useMapSelectionCore({
    geoItemsMeta: deps.geoItemsMeta,
    refExtremeMap: deps.refExtremeMap,
    resizeMap: deps.resizeMap,
    simpleModal: deps.simpleModal,
    afterMoviePlayClick: deps.afterMoviePlayClick,
    doAreaMovieSearch: deps.doAreaMovieSearch,
    isMovieSearchResultModeNone: deps.isMovieSearchResultModeNone,
    isMovieSearchResultModeArea: deps.isMovieSearchResultModeArea,
    getAreaQueryRadiusMeter: deps.getAreaQueryRadiusMeter,
  });

  const commentCrud = useMapSelectionCommentCrud({
    geoItemsMeta: deps.geoItemsMeta,
    mapElem: core.mapElem,
    setMapSelectedElemInfo: core.setMapSelectedElemInfo,
    refreshCommentTab: deps.refreshCommentTab,
    getCommentSearchRef: deps.getCommentSearchRef,
  });

  const geoItemNav = useMapSelectionGeoItemNav({
    geoItemsMeta: deps.geoItemsMeta,
    mapElem: core.mapElem,
    setMapSelectedElemInfo: core.setMapSelectedElemInfo,
    afterDataLayerItemClick: core.afterDataLayerItemClick,
    changeGeoItemLayers: deps.changeGeoItemLayers,
  });

  const store = {
    mapElem: core.mapElem,
    setMapSelectedElemInfo: core.setMapSelectedElemInfo,
    tryRefreshCurrentSelectedElemInfo: core.tryRefreshCurrentSelectedElemInfo,
    afterDataLayerItemClick: core.afterDataLayerItemClick,
    currentElemInfoComponent: core.currentElemInfoComponent,
    getNewObjForMapSelectedElem: core.getNewObjForMapSelectedElem,
    onClickMap: core.onClickMap,
    onMapItemClicked: core.onMapItemClicked,
    onMapAllDeselected: core.onMapAllDeselected,
    createMapSelectedElem: commentCrud.createMapSelectedElem,
    updateMapSelectedElem: commentCrud.updateMapSelectedElem,
    confirmDeleteMapSelectedElem: commentCrud.confirmDeleteMapSelectedElem,
    doDeleteMapSelectedElem: commentCrud.doDeleteMapSelectedElem,
    cancelDeleteMapSelectedElem: commentCrud.cancelDeleteMapSelectedElem,
    playMapSelectedElem: core.playMapSelectedElem,
    onMapSelectedElemAreaSizeChange: core.onMapSelectedElemAreaSizeChange,
    onTabElementClick: geoItemNav.onTabElementClick,
    showPreviousGeoItem: geoItemNav.showPreviousGeoItem,
    showNextGeoItem: geoItemNav.showNextGeoItem,
    getGeoItemById: geoItemNav.getGeoItemById,
  };
  provideMapSelectionCtx(store);
  return store;
}
